import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/axiosConfig';
import {
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  Squares2X2Icon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const MODULE_OPTIONS = [
  { value: 'crm',      label: 'CRM & Leads',     color: 'bg-purple-100 text-purple-700' },
  { value: 'projects', label: 'Projects',          color: 'bg-blue-100 text-blue-700' },
  { value: 'tasks',    label: 'Tasks & Kanban',    color: 'bg-sky-100 text-sky-700' },
  { value: 'vendors',  label: 'Vendors',           color: 'bg-amber-100 text-amber-700' },
  { value: 'assets',   label: 'Assets & Equipment',color: 'bg-rose-100 text-rose-700' },
];

const FIELD_TYPES = [
  { value: 'text',     label: 'Text (Single line)' },
  { value: 'textarea', label: 'Text (Multi-line)' },
  { value: 'number',   label: 'Number' },
  { value: 'date',     label: 'Date' },
  { value: 'select',   label: 'Dropdown / Select' },
  { value: 'checkbox', label: 'Checkbox (Yes/No)' },
];

const EMPTY_FORM = {
  field_label: '',
  field_type: 'text',
  options: '',
  is_required: false,
};

// Toast notification component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium transition-all animate-fade-in-up ${
      type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-500 text-white'
    }`}>
      {type === 'success'
        ? <CheckCircleIcon className="w-5 h-5 shrink-0" />
        : <ExclamationTriangleIcon className="w-5 h-5 shrink-0" />}
      {message}
    </div>
  );
};

// Field type badge
const TypeBadge = ({ type }) => {
  const colors = {
    text:     'bg-gray-100 text-gray-600',
    textarea: 'bg-gray-100 text-gray-600',
    number:   'bg-blue-50 text-blue-700',
    date:     'bg-indigo-50 text-indigo-700',
    select:   'bg-purple-50 text-purple-700',
    checkbox: 'bg-emerald-50 text-emerald-700',
  };
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${colors[type] || 'bg-gray-100 text-gray-500'}`}>
      {FIELD_TYPES.find(f => f.value === type)?.label || type}
    </span>
  );
};

export default function CustomFieldSettings() {
  const [selectedModule, setSelectedModule] = useState('crm');
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => setToast({ message, type });

  /// ── Fetch fields for selected module ──────────────────────────────────────
  const fetchFields = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/custom-fields?module=${selectedModule}`);
      setFields(Array.isArray(data) ? data : (data?.data || []));
    } catch (error) {
      console.error("Settings Fetch Error:", error);
      // Demo fallback
      setFields([
        { id: 1, field_label: 'Lead Source', field_type: 'select', options: ['Website','Referral','Cold Call','Exhibition'], is_required: true },
        { id: 2, field_label: 'Expected Close Date', field_type: 'date', options: [], is_required: false },
        { id: 3, field_label: 'Notes / Remarks', field_type: 'textarea', options: [], is_required: false },
      ]);
    } finally {
      setLoading(false);
    }
  }, [selectedModule]);

  useEffect(() => { fetchFields(); }, [fetchFields]);

  // ── Create field ───────────────────────────────────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.field_label.trim()) return showToast('Field label is required.', 'error');

    const payload = {
      module_name: selectedModule,
      field_label: form.field_label.trim(),
      field_type:  form.field_type,
      is_required: form.is_required,
      options: form.field_type === 'select'
        ? form.options.split(',').map(s => s.trim()).filter(Boolean)
        : [],
    };

    setSaving(true);
    try {
      const { data } = await api.post('/custom-fields', payload);
      const newField = data?.data || data;
      setFields(prev => [...prev, newField]);
      setForm(EMPTY_FORM);
      showToast('Custom field created successfully!');
    } catch (error) {
      console.error("Settings Create Error:", error);
      // Demo fallback
      setFields(prev => [...prev, { ...payload, id: Date.now(), options: payload.options }]);
      setForm(EMPTY_FORM);
      showToast('Field added (demo mode — backend not connected).', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete field ───────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/custom-fields/${id}`);
      setFields(prev => prev.filter(f => f.id !== id));
      showToast('Field deleted.');
    } catch (error) {
      console.error("Settings Delete Error:", error);
    } finally {
      setDeletingId(null);
    }
  };
  const currentModule = MODULE_OPTIONS.find(m => m.value === selectedModule);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 animate-fade-in-up overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-purple-100 rounded-lg">
              <Squares2X2Icon className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Custom Field Definitions</h3>
              <p className="text-sm text-gray-500 mt-0.5">Add extra data fields to any module without touching the database schema.</p>
            </div>
          </div>

          {/* Module selector */}
          <select
            value={selectedModule}
            onChange={e => setSelectedModule(e.target.value)}
            className="w-full sm:w-52 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            {MODULE_OPTIONS.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-0 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">

        {/* ── LEFT: Existing Fields List ─────────────────────────────────── */}
        <div className="flex-1 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className={`text-xs font-bold px-2 py-0.5 rounded ${currentModule?.color}`}>
                {currentModule?.label}
              </span>
              <span className="text-xs text-gray-400">{fields.length} field{fields.length !== 1 ? 's' : ''}</span>
            </div>
            <button onClick={fetchFields} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition" title="Refresh">
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => (
                <div key={i} className="h-14 bg-gray-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : fields.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 text-center">
              <div className="p-4 bg-gray-100 rounded-full mb-3">
                <Squares2X2Icon className="w-8 h-8 text-gray-300" />
              </div>
              <p className="text-sm font-medium text-gray-500">No custom fields yet</p>
              <p className="text-xs text-gray-400 mt-1">Create your first field using the form →</p>
            </div>
          ) : (
            <ul className="space-y-2.5">
              {fields.map(field => (
                <li key={field.id} className="group flex items-center justify-between gap-3 p-3.5 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-white hover:border-gray-200 hover:shadow-sm transition-all duration-200">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-800 truncate">{field.field_label}</span>
                      {field.is_required && (
                        <span className="text-[10px] font-bold text-red-500 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded">Required</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <TypeBadge type={field.field_type} />
                      {field.field_type === 'select' && Array.isArray(field.options) && field.options.length > 0 && (
                        <span className="text-[10px] text-gray-400">
                          {field.options.slice(0, 3).join(', ')}{field.options.length > 3 ? ` +${field.options.length - 3} more` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(field.id)}
                    disabled={deletingId === field.id}
                    className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all duration-200 shrink-0"
                    title="Delete field"
                  >
                    {deletingId === field.id
                      ? <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      : <TrashIcon className="w-4 h-4" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── RIGHT: Create Form ─────────────────────────────────────────── */}
        <div className="lg:w-96 p-6 bg-gray-50/50">
          <h4 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
            <PlusIcon className="w-4 h-4 text-purple-600" />
            Add New Field
          </h4>

          <form onSubmit={handleCreate} className="space-y-4">
            {/* Field Label */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Field Label <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={form.field_label}
                onChange={e => setForm(p => ({ ...p, field_label: e.target.value }))}
                placeholder="e.g. Lead Source, Budget Range…"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
              />
            </div>

            {/* Field Type */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Field Type
              </label>
              <select
                value={form.field_type}
                onChange={e => setForm(p => ({ ...p, field_type: e.target.value, options: '' }))}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
              >
                {FIELD_TYPES.map(ft => (
                  <option key={ft.value} value={ft.value}>{ft.label}</option>
                ))}
              </select>
            </div>

            {/* Conditional: Options for select */}
            {form.field_type === 'select' && (
              <div className="animate-fade-in-up">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Dropdown Options <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={form.options}
                  onChange={e => setForm(p => ({ ...p, options: e.target.value }))}
                  placeholder="Website, Referral, Cold Call, Exhibition"
                  rows={3}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition resize-none"
                />
                <p className="text-xs text-gray-400 mt-1">Enter options separated by commas.</p>
              </div>
            )}

            {/* Is Required Toggle */}
            <div className="flex items-center justify-between p-3.5 rounded-lg bg-white border border-gray-200">
              <div>
                <p className="text-sm font-medium text-gray-700">Required Field</p>
                <p className="text-xs text-gray-400">Users must fill this field before saving.</p>
              </div>
              <button
                type="button"
                onClick={() => setForm(p => ({ ...p, is_required: !p.is_required }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${
                  form.is_required ? 'bg-purple-600' : 'bg-gray-300'
                }`}
              >
                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                  form.is_required ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </button>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white text-sm font-bold transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
            >
              {saving
                ? <><ArrowPathIcon className="w-4 h-4 animate-spin" /> Saving…</>
                : <><PlusIcon className="w-4 h-4" /> Create Custom Field</>}
            </button>
          </form>
        </div>
      </div>

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}